import Vue from 'vue';
import Router from 'vue-router';
import store from '@/store/index';
import { CreateElement } from 'vue/types/umd';
// @ts-ignore
import NProgress from 'nprogress/nprogress';

Vue.use(Router);

const router = new Router({
  mode: 'history',
  base: process.env.BASE_URL,

  /*
   * | ------------------------
   * | Simulate native-like scroll behavior when navigating to a new
   * | route and using back/forward buttons.
   * | ------------------------
   * |
   */
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { x: 0, y: 0 };
    }
  },

  /*
   * | ------------------------
   * | All router page routes
   * | Contains all routes and their matching components
   * | ------------------------
   * |
   */
  routes: [
    {
      path: '/index.html',
      alias: '/',
      beforeEnter: guardGuest,
      component: loadView('guest/login'),
    },
    {
      path: '/',
      name: 'login',
      beforeEnter: guardGuest,
      component: loadView('guest/login'),
    },
    {
      path: '/guest/password/forgot',
      name: 'password-forgot',
      beforeEnter: guardGuest,
      component: loadView('guest/password-forgot'),
    },
    {
      path: '/guest/password/reset',
      name: 'password-reset',
      beforeEnter: guardGuest,
      component: loadView('guest/password-reset'),
    },
    {
      path: '/auth/search',
      name: 'search-results',
      beforeEnter: guardAuth,
      component: loadView('auth/search'),
    },
    {
      path: '/auth/dashboard',
      name: 'dashboard',
      beforeEnter: guardAuth,
      component: loadView('auth/dashboard'),
      meta: {
        title: 'FVG Dashboard',
      },
    },
    {
      path: '/auth/active/',
      beforeEnter: guardAuth,
      component: loadView('auth/active/index'),
      children: [
        {
          path: 'dossiers',
          name: 'active-dossiers',
          component: loadView('auth/active/index'),
          meta: {
            type: 'file',
            page: 'active',
            title: 'FVG - Active dossiers',
            beforeResolve(to: any, from: any, next: any) {
              if (store.getters['dossiers/active'].Results.length > 0) {
                next();
              } else {
                store.dispatch('dossiers/readActive').then(() => {
                  next();
                });
              }
            },
          },
        },
        {
          path: 'goods',
          name: 'active-goods',
          component: loadView('auth/active/index'),
          meta: {
            type: 'good',
            page: 'active',
            title: 'FVG - Active transports',
            beforeResolve(to: any, from: any, next: any) {
              if (store.getters['goods/active'].Results.length > 0) {
                next();
              } else {
                store.dispatch('goods/readActive').then(() => {
                  next();
                });
              }
            },
          },
        },
      ],
    },
    {
      path: '/auth/active/transports',
      name: 'active-transports',
      component: loadView('auth/active/transports'),
      meta: {
        type: 'transport',
        page: 'active',
        title: 'FVG - Active transports',
        beforeResolve(to: any, from: any, next: any) {
          if (store.getters['transports/searched'].Results.length > 0) {
            next();
          } else {
            store.dispatch('lookup/transportmodes');
            store.dispatch('lookup/activegoodsuppliers');
            store.dispatch('lookup/carriers');
            store.dispatch('transports/search', { Page: 1 }).then(() => {
              next();
            });
          }
        },
      },
    },

    {
      path: '/auth/planning/import',
      name: 'planning-import',
      beforeEnter: guardAuth,
      component: loadView('auth/planning/import'),
      meta: {
        page: 'planning',
        title: 'FVG - Planning',
        beforeResolve(to: any, from: any, next: any) {
          const promises = [];

          promises.push(store.dispatch('transports/readImportPlanning', 1));
          promises.push(store.dispatch('lookup/deliverylocations'));

          Promise.all(promises).then(() => {
            next();
          });
        },
      },
    },
    {
      path: '/auth/planning/export',
      name: 'planning-export',
      beforeEnter: guardAuth,
      component: loadView('auth/planning/export'),
      meta: {
        page: 'planning',
        title: 'FVG - Planning',
      },
    },

    {
      path: '/auth/archive',
      name: 'archive',
      beforeEnter: guardAuth,
      component: loadView('auth/archive'),
    },
    {
      path: '/auth/invoices',
      name: 'invoices',
      beforeEnter: guardAuth,
      component: loadView('auth/invoices'),
    },
    {
      path: '/auth/settings',
      name: 'settings',
      beforeEnter: guardAuth,
      component: loadView('auth/settings'),
    },
    {
      path: '/auth/settings/password/reset',
      name: 'auth-password-reset',
      beforeEnter: guardAuth,
      component: loadView('auth/password-reset'),
    },
    {
      path: '/auth/dossiers/:id',
      name: 'dossier-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/dossiers/detail'),
      meta: {
        title: 'FVG - Dossier detail',
        beforeResolve(to: any, from: any, next: any) {
          store.dispatch('dossiers/read', to.params.id).then(() => {
            next();
          });
        },
      },
    },
    {
      path: '/auth/transports/:id',
      name: 'transport-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/transports/detail'),
      meta: {
        title: 'FVG - Transport detail',
        beforeResolve(to: any, from: any, next: any) {
          store.dispatch('lookup/carriers');
          store.dispatch('transports/read', to.params.id).then(() => {
            next();
          });
        },
      },
    },
    {
      path: '/auth/goods/:id',
      name: 'good-detail',
      beforeEnter: guardAuth,
      component: loadView('auth/goods/detail'),
      meta: {
        title: 'FVG - Good detail',
        beforeResolve(to: any, from: any, next: any) {
          store.dispatch('goods/read', to.params.id).then(() => {
            next();
            store.dispatch('goods/readContainers', to.params.id);
            store.dispatch('goods/readTransports', to.params.id);
          });
        },
      },
    },
    {
      path: '/auth/logout',
      name: 'logout',
      beforeEnter: guardLogout,
    },
  ],
});

router.beforeEach(async (routeTo: any, routeFrom: any, next: any) => {
  if (routeFrom.name !== null) {
    NProgress.start();
  }
  next();
});

router.afterEach(() => {
  NProgress.done();
});

router.beforeResolve(async (routeTo: any, routeFrom: any, next: any) => {
  try {
    if (routeFrom.meta && routeFrom.meta.leaveCallback) {
      await routeFrom.meta.leaveCallback(routeTo, routeFrom);
    }

    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        if (route.meta && route.meta.title) {
          document.title = route.meta.title;
        }

        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args: any) => {
            if (args.length) {
              if (routeFrom.name === args[0].name) {
                NProgress.done();
              }
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          resolve();
        }
      });
    }
  } catch (error) {
    return;
  }

  next();
});

/**
 * Guard guest middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardGuest(to: any, from: any, next: any) {
  if (store.getters['user/isLoggedIn']) {
    return next({ name: 'dashboard' });
  }

  next();
}

/**
 * Guard auth middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardAuth(to: any, from: any, next: any) {
  if (!store.getters['user/isLoggedIn']) {
    next({ name: 'login' });
  }

  if (!store.getters['user/auth']) {
    store.dispatch('user/me').catch(e => {
      if (e === 401) {
        store.dispatch('reset').then(() => {
          return next({ name: 'login' });
        });
      }
    });
  }
  if (store.getters['lookup/filestatuses'].length === 0) {
    store.dispatch('lookup/filestatuses');
  }
  if (store.getters['lookup/transportmodes'].length === 0) {
    store.dispatch('lookup/transportmodes');
  }
  if (store.getters['lookup/documenttypes'].length === 0) {
    store.dispatch('lookup/documenttypes');
  }

  next();
}

/**
 * Guard logout middleware
 * Redirect to the login page when the user is not logged in
 *
 * @param {to} string The route the user was comming from
 * @param {from} string The route the user is going to
 * @param {next} function The next route funciton
 */
function guardLogout(to: any, from: any, next: any) {
  if (store.getters['user/isLoggedIn']) {
    store.dispatch('reset');
  }

  next({ name: 'login' });
}

function loadView(view: string) {
  return () => lazyLoadView(import(`@/views/${view}.vue`));
}

/**
 * Lazy-loads view components, but with better UX. A loading view
 * will be used if the component takes a while to load, falling
 * back to a timeout view in case the page fails to load. You can
 * use this component to lazy-load a route with:
 *
 * component: () => lazyLoadView(import('@views/my-view'))
 *
 * @param {AsyncView} The view component
 */
function lazyLoadView(AsyncView: any) {
  const AsyncHandler = () => ({
    component: AsyncView,
    delay: 400,
    timeout: 10000,
  });

  return Promise.resolve({
    functional: true,
    render(h: CreateElement, { data, children }: any) {
      return h(AsyncHandler, data, children);
    },
  });
}

export default router;
